import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
} from "@tanstack/react-query";
import { useReadyAuth } from "@/auth";
import { useActionsAdapter, useFeedAdapter } from "./use-adapters";
import type {
  ActionHistoryEntry,
  ArchiveReason,
  AssignableUser,
  CanonicalPractice,
  FeedPage,
  FeedSignal,
  FeedTab,
  PracticeKey,
  PracticeLensSummary,
  SignalTypeFilter,
  SortValue,
  TabCounts,
  TriageCounts,
} from "./types";

/**
 * React Query hooks for the signals feed. Keys are namespaced under "signals"
 * so a claim/assign can invalidate the feed, tab counts, and triage strip in
 * one sweep. All data flows through the injected adapter (mock in Phase D).
 */

export const FEED_PAGE_SIZE = 25;

export type FeedFooterMode = "more" | "end" | "none";

/**
 * Which footer the feed renders below the cards. The "Show more" control is
 * gated PURELY on hasNextPage (React Query's keyset cursor state) — never on a
 * counts.all-derived remaining value, which can outlast the cursor walk and
 * leave a dead, clickable-but-no-op button. When the walk is exhausted and rows
 * exist, the feed shows an honest end-of-list line instead.
 */
export function feedFooterMode(
  hasNextPage: boolean,
  signalCount: number,
): FeedFooterMode {
  if (hasNextPage) return "more";
  if (signalCount > 0) return "end";
  return "none";
}

export const keys = {
  feed: (
    tab: FeedTab,
    viewerId: string,
    signalType: SignalTypeFilter,
    sort: SortValue,
    practiceKey: PracticeKey | null,
  ) =>
    ["signals", "feed", tab, viewerId, signalType, sort, practiceKey] as const,
  triage: (viewerId: string) => ["signals", "triage", viewerId] as const,
  tabCounts: (viewerId: string) => ["signals", "tabCounts", viewerId] as const,
  detail: (id: string) => ["signals", "detail", id] as const,
  history: (signalId: string) => ["signals", "history", signalId] as const,
  /** Loaded once per authenticated session — the registry is the same for
   *  every viewer, so the key carries no viewer id. */
  practices: ["signals", "practices"] as const,
};

export interface UseSignalFeedResult {
  signals: FeedSignal[];
  /** Total rows matching the active tab + type filter (for "Show 5 more"). */
  total: number;
  /** Present only while a practice is selected — the ONLY honest source for
   *  "how many signals are routed to this practice". Null under "All". */
  practiceLens: PracticeLensSummary | null;
  status: "pending" | "error" | "success";
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  refetch: () => void;
}

export function useSignalFeed(
  tab: FeedTab,
  viewerId: string,
  signalType: SignalTypeFilter,
  sort: SortValue,
  practiceKey: PracticeKey | null,
): UseSignalFeedResult {
  const feed = useFeedAdapter();
  const query = useInfiniteQuery({
    queryKey: keys.feed(tab, viewerId, signalType, sort, practiceKey),
    queryFn: ({ pageParam }) =>
      feed.getFeedPage({
        tab,
        viewerId,
        signalType,
        sort,
        practiceKey,
        cursor: pageParam,
        limit: FEED_PAGE_SIZE,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: Boolean(viewerId),
  });

  return {
    signals: query.data?.pages.flatMap((p) => p.signals) ?? [],
    total: query.data?.pages[0]?.total ?? 0,
    practiceLens: query.data?.pages[0]?.practiceLens ?? null,
    status: query.status,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage,
    fetchNextPage: () => {
      void query.fetchNextPage();
    },
    refetch: () => {
      void query.refetch();
    },
  };
}

/**
 * The active canonical peer practices behind the Signals practice dropdown
 * (2W). Loaded once per authenticated session: `staleTime: Infinity` and no
 * window-focus refetch, since the registry does not change within a session
 * and every consumer (the dropdown, the URL validator) shares this one query.
 */
export function usePracticeRegistry(viewerId: string) {
  const feed = useFeedAdapter();
  return useQuery<CanonicalPractice[]>({
    queryKey: keys.practices,
    queryFn: () => feed.listPractices(),
    enabled: Boolean(viewerId),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}

export function useTriageCounts(viewerId: string) {
  const feed = useFeedAdapter();
  return useQuery<TriageCounts>({
    queryKey: keys.triage(viewerId),
    queryFn: () => feed.getTriageCounts(viewerId),
    enabled: Boolean(viewerId),
  });
}

export function useTabCounts(viewerId: string) {
  const feed = useFeedAdapter();
  return useQuery<TabCounts>({
    queryKey: keys.tabCounts(viewerId),
    queryFn: () => feed.getTabCounts(viewerId),
    enabled: Boolean(viewerId),
  });
}

/**
 * The row for this signal is already sitting in the feed cache — the viewer just
 * clicked it. Find it so the modal can paint from it on the same tick instead of
 * holding a blank shell open for the whole get_signal_detail round trip.
 *
 * A feed row carries score, tier, company, headline, ownership, saved and
 * follow-up. It does NOT carry signal_content (summary, why-it-matters,
 * recommended action, talk track) — the detail RPC is the only source for those,
 * so they arrive empty here and the modal skeletons them until the fetch lands.
 */
function signalFromFeedCache(
  qc: QueryClient,
  id: string,
): FeedSignal | undefined {
  const entries = qc.getQueriesData<InfiniteData<FeedPage>>({
    queryKey: ["signals", "feed"],
  });
  for (const [, data] of entries) {
    for (const page of data?.pages ?? []) {
      const hit = page.signals.find((s) => s.id === id);
      if (hit) return hit;
    }
  }
  return undefined;
}

export function useSignalDetail(id: string | null, viewerId: string) {
  const feed = useFeedAdapter();
  const qc = useQueryClient();
  return useQuery<FeedSignal | null>({
    queryKey: [...keys.detail(id ?? ""), viewerId],
    queryFn: () => feed.getSignal(id as string, viewerId),
    enabled: Boolean(id),
    staleTime: 0,
    refetchOnMount: "always",
    // Paints the modal instantly from the card the viewer clicked. Undefined on a
    // cold deep link (no feed cache), where the modal falls back to its skeleton.
    placeholderData: () => (id ? signalFromFeedCache(qc, id) : undefined),
  });
}

/**
 * M5 AssignPopover roster — active teammates the viewer can assign to. Mock
 * returns a fixed set; live reads active user_profiles (excluding the viewer).
 */
export function useAssignableUsers() {
  const feed = useFeedAdapter();
  const { user } = useReadyAuth();
  return useQuery<AssignableUser[]>({
    queryKey: ["signals", "assignableUsers", user.id],
    queryFn: () => feed.listAssignableUsers(user.id),
  });
}

/** Card CTA "Claim Signal" — claim, then refresh the whole signals surface. */
export function useClaimSignal() {
  const actions = useActionsAdapter();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (signalId: string) => actions.claim(signalId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["signals"] });
    },
  });
}

/** Card "Unclaim" — release a claim, then refresh the whole signals surface. */
export function useUnclaimSignal() {
  const actions = useActionsAdapter();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (signalId: string) => actions.unclaim(signalId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["signals"] });
    },
  });
}

/**
 * Card/modal star toggle — flips the viewer's personal saved state, then
 * refreshes the signals surface so the Saved tab + Saved stat update.
 */
export function useToggleSave() {
  const actions = useActionsAdapter();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (signalId: string) => actions.toggleSave(signalId),
    onMutate: async (signalId) => {
      await qc.cancelQueries({ queryKey: ["signals"] });
      const previous = qc.getQueriesData({ queryKey: ["signals"] });
      qc.setQueriesData({ queryKey: ["signals"] }, (old) =>
        toggleSavedInSignalsCache(old, signalId),
      );
      return { previous };
    },
    onError: (_err, _signalId, context) => {
      context?.previous.forEach(([queryKey, data]) => {
        qc.setQueryData(queryKey, data);
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["signals"] });
    },
  });
}

function toggleSavedInSignalsCache(value: unknown, signalId: string): unknown {
  if (Array.isArray(value)) {
    return value.map((item) => toggleSavedInSignalsCache(item, signalId));
  }
  if (!value || typeof value !== "object") return value;

  const record = value as Record<string, unknown>;
  const next: Record<string, unknown> = {};
  let changed = false;
  for (const [key, nested] of Object.entries(record)) {
    const updated = toggleSavedInSignalsCache(nested, signalId);
    next[key] = updated;
    if (updated !== nested) changed = true;
  }

  if (record.id === signalId && typeof record.saved === "boolean") {
    next.saved = !record.saved;
    changed = true;
  }

  return changed ? next : value;
}

/** Header "Export CSV" (admin/operator) — stub adapter call in Phase D. */
export function useExportFeedCsv() {
  const feed = useFeedAdapter();
  return useMutation({
    mutationFn: (params: {
      tab: FeedTab;
      signalType: SignalTypeFilter;
      viewerId: string;
      practiceKey: PracticeKey | null;
    }) => feed.exportFeedCsv(params),
  });
}

/** M18 timeline — read-only, via get_signal_action_history (spec §4). */
export function useActionHistory(signalId: string | null) {
  const actions = useActionsAdapter();
  return useQuery<ActionHistoryEntry[]>({
    queryKey: keys.history(signalId ?? ""),
    queryFn: () => actions.getActionHistory(signalId as string),
    enabled: Boolean(signalId),
  });
}

/**
 * All modal action mutations bound to one signal (spec §4 Edge Function map,
 * mock in Phase D). Each invalidates the signals namespace so the feed, detail,
 * and timeline reflect the change. Every useMutation is called unconditionally
 * so the hook order is stable.
 */
export function useSignalActions(signalId: string) {
  const actions = useActionsAdapter();
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["signals"] });
  };
  return {
    claim: useMutation({
      mutationFn: () => actions.claim(signalId),
      onSuccess: invalidate,
    }),
    unclaim: useMutation({
      mutationFn: () => actions.unclaim(signalId),
      onSuccess: invalidate,
    }),
    assign: useMutation({
      mutationFn: (v: { assigneeId: string; note?: string }) =>
        actions.assign(signalId, v.assigneeId, v.note),
      onSuccess: invalidate,
    }),
    share: useMutation({
      mutationFn: (recipientId: string) => actions.share(signalId, recipientId),
      onSuccess: invalidate,
    }),
    archive: useMutation({
      mutationFn: (reason: ArchiveReason) =>
        actions.archiveWithReason(signalId, reason),
      onSuccess: invalidate,
    }),
    setFollowUp: useMutation({
      mutationFn: (dueAt: string) => actions.setFollowUp(signalId, dueAt),
      onSuccess: invalidate,
    }),
    relationshipCheck: useMutation({
      mutationFn: () => actions.runRelationshipCheck(signalId),
      onSuccess: invalidate,
    }),
    tariffScan: useMutation({
      mutationFn: () => actions.runTariffScan(signalId),
      onSuccess: invalidate,
    }),
    sendAsset: useMutation({
      mutationFn: (assetId: string) => actions.sendAsset(signalId, assetId),
      onSuccess: invalidate,
    }),
    // Optimistic, matching the card's useToggleSave. Without onMutate the modal
    // star sat unflipped and disabled for the whole round trip (3-5s on blue)
    // while the card's flipped on the same tick — the reported asymmetry.
    // toggleSavedInSignalsCache walks every "signals" query, so this rewrites the
    // detail cache the modal reads AND the feed cache behind it in one pass.
    toggleSave: useMutation({
      mutationFn: () => actions.toggleSave(signalId),
      onMutate: async () => {
        await qc.cancelQueries({ queryKey: ["signals"] });
        const previous = qc.getQueriesData({ queryKey: ["signals"] });
        qc.setQueriesData({ queryKey: ["signals"] }, (old) =>
          toggleSavedInSignalsCache(old, signalId),
        );
        return { previous };
      },
      // Reconcile by reverting to exactly what was there before the flip. The
      // star snaps back and ModalHeader surfaces a quiet inline error.
      onError: (_err, _vars, context) => {
        context?.previous.forEach(([queryKey, data]) => {
          qc.setQueryData(queryKey, data);
        });
      },
      onSuccess: invalidate,
    }),
    // Pursuit stage advance / walk-back (record_stage_event, roster JWT path).
    // Invalidates so the detail lifecycle + action history reflect the new stage.
    recordStage: useMutation({
      mutationFn: (v: {
        eventType:
          | "stage_outreach_made"
          | "stage_meeting_scheduled"
          | "stage_proposal"
          | "stage_regressed";
        toStage?: "assigned" | "stage_outreach_made" | "stage_meeting_scheduled";
        reason?: string;
      }) =>
        actions.recordStage(signalId, v.eventType, {
          toStage: v.toStage,
          reason: v.reason,
        }),
      onSuccess: invalidate,
    }),
    // Won / Passed / Reopen (record_outcome, roster JWT path). Invalidates so the
    // detail lifecycle_status (→ closed badge, Reopen) and history update.
    recordOutcome: useMutation({
      mutationFn: (v: { outcome: "won" | "passed" | "reopen"; reason?: string }) =>
        actions.recordOutcome(signalId, v.outcome, v.reason),
      onSuccess: invalidate,
    }),
    // Seamless find-contacts (find_contacts_proxy). Results live in the mutation
    // data; no invalidation so the results panel is stable while open.
    findContacts: useMutation({
      mutationFn: (v: { refresh?: boolean } | void) =>
        actions.findContacts(signalId, v ?? undefined),
    }),
    saveFoundContact: useMutation({
      mutationFn: (foundContactId: string) =>
        actions.saveFoundContact(signalId, foundContactId),
    }),
    // Restore an archived signal (R-4, restore-signal). Invalidates so the
    // detail lifecycle_status, the archived chip and the feed tab counts all
    // reflect the signal returning to active. Inert until the live-call gate
    // opens; see RESTORE_LIVE_CALL_ENABLED.
    restoreSignal: useMutation({
      mutationFn: () => actions.restoreSignal(signalId),
      onSuccess: invalidate,
    }),
  };
}

export type SignalActionMutations = ReturnType<typeof useSignalActions>;
